import { consume } from '@lit/context';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { toolbarChip } from '../../../theme/toolbar.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { MOBILE_BREAKPOINT_PX } from '../layout';

/**
 * Airmash Logo — clickable header mark.
 *
 * Desktop: a link home (returns to the lobby), shown as the white-backed
 * square mark (portrait) or the full word mark (>=768px).
 *
 * Mobile: the game-selector chip is dropped to save top-bar space, so the
 * logo becomes that panel's trigger — rendered as a 44×44 toolbar chip
 * matching the action chips (the Λ mark recoloured white on the dark chip),
 * with a caret that flips when the panel is open. It fires the same
 * `toggle-game-selector` event the F2 keybind uses; the panel's own CHANGE
 * REGION button is the return-to-lobby path there.
 *
 * @element airmash-logo
 */
@customElement('airmash-logo')
export class AirmashLogo extends LitElement {
  static override styles = [
    tokens,
    toolbarChip,
    css`
      :host {
        display: block;
        pointer-events: auto;
        flex: 0 0 auto;
        /* Declared here rather than inherited from the body — a long-press on
           the mark mustn't raise iOS's "save image" callout, a selection or a
           grey tap flash. */
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      a {
        display: block;
      }

      /* Desktop link mark: white-backed square (portrait) / word mark (>=768).
         favicon.svg renders the Λ in black on transparent, which disappears
         against the dark canvas — the white pad gives it contrast. border-box
         keeps the chip at 28×28. */
      a img {
        box-sizing: border-box;
        display: block;
        width: 28px;
        height: 28px;
        background: var(--color-text-white);
        padding: 3px;
        border-radius: var(--radius-sm);
      }

      @media (min-width: ${unsafeCSS(MOBILE_BREAKPOINT_PX)}px) {
        a img {
          /* Tablet/desktop: full word mark at 104×21 (the file is 416×84, 4×
             for sharp high-DPI screens), no chip — it's white on transparent
             and reads on the dark canvas. */
          width: 104px;
          height: 21px;
          background: transparent;
          padding: 0;
        }
      }

      /* Caret on the mobile trigger — signals "opens a menu" and flips to
         point up while the panel is open. */
      .caret {
        flex: 0 0 auto;
        width: 5px;
        height: 5px;
        margin-left: 1px;
        border-right: 2px solid var(--color-text-white);
        border-bottom: 2px solid var(--color-text-white);
        transform: rotate(45deg);
        transition: transform 0.2s ease;
        opacity: 0.8;
      }

      .caret.open {
        transform: rotate(225deg);
      }
    `,
  ];

  /** UI state — `isMobile` picks link-home vs trigger; `openTopBarPanel`
   *  flips the caret while the selector panel is open. */
  private uiState = bindValtio(this, uiStateProxy);

  /** Game API from context */
  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  /** Opens the game-selector via the shared toggle event. stopPropagation so
   *  game-selector's document-level outside-click handler doesn't see this
   *  click and immediately re-close the panel we're opening. The click sound
   *  is played here, not by game-selector: the same event serves the F2
   *  keybind, which stays silent like other key toggles. */
  private handleClick = (e: Event) => {
    e.stopPropagation();
    this.gameApi.playClick();
    window.dispatchEvent(new Event('toggle-game-selector'));
  };

  override render() {
    if (this.uiState.isMobile) {
      const open = this.uiState.openTopBarPanel === 'game-selector';
      return html`
        <button
          class="toolbar-chip"
          @click=${this.handleClick}
          aria-label="Game menu"
          aria-haspopup="menu"
          aria-expanded=${open}
        >
          <img src="/favicon.svg" width="28" height="28" alt="Airmash" />
          <span class="caret ${open ? 'open' : ''}" aria-hidden="true"></span>
        </button>
      `;
    }

    /* <picture> + <source media> swaps the file the browser downloads: below
       the breakpoint only favicon.svg, at/above it only logo.png. CSS sizes it.
       The media query must use the SAME MOBILE_BREAKPOINT_PX as the CSS rule
       above, or the file swap and the size swap would happen at different
       widths. draggable="false" on both the link and the image (each is
       draggable by default) stops a mouse drag pulling a ghost of the mark.
       It's the attribute rather than the chips' -webkit-user-drag, which
       Firefox doesn't support. */
    return html`
      <a href="/" target="_top" draggable="false">
        <picture>
          <source
            media="(min-width: ${MOBILE_BREAKPOINT_PX}px)"
            srcset="/vanilla/ui/logo.png"
            width="104"
            height="21"
          />
          <img src="/favicon.svg" width="28" height="28" alt="Airmash" draggable="false" />
        </picture>
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'airmash-logo': AirmashLogo;
  }
}
